import { useState, useEffect } from "react";
import { X, ExternalLink, RefreshCw, AlertTriangle, CheckCircle2, Video } from "lucide-react";
import { cn } from "@/lib/cn";
import { formatDate } from "@/lib/format";
import type { SourceLead, FathomQCItem, FathomQCResponse } from "@/lib/types";

export function statusBadgeClasses(status: string): string {
  const s = status.toLowerCase();
  if (!s) return "bg-slate-100 text-slate-500";
  if (s.includes("won")) return "bg-green-100 text-green-700";
  if (s.includes("lost") || s.includes("junk") || s.includes("disqualif") || s.includes("unqualif")) {
    return "bg-rose-100 text-rose-700";
  }
  if (s.includes("not contacted") || s.includes("no response") || s.includes("attempted")) {
    return "bg-amber-100 text-amber-700";
  }
  if (
    s.includes("scheduled") ||
    s.includes("demo") ||
    s.includes("discovery") ||
    s.includes("qualified") ||
    s.includes("sql") ||
    s.includes("sqo") ||
    s.includes("negotiation") ||
    s.includes("proposal")
  ) {
    return "bg-blue-100 text-blue-700";
  }
  if (s.includes("contacted") || s.includes("nurtur") || s.includes("follow")) {
    return "bg-cyan-100 text-cyan-700";
  }
  return "bg-slate-100 text-slate-700";
}

export function StatusBadge({ status }: { status: string }) {
  const label = status?.trim() || "—";
  return (
    <span
      title={label}
      className={cn(
        "inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold tracking-wide whitespace-nowrap",
        statusBadgeClasses(label)
      )}
    >
      {label}
    </span>
  );
}

export function formatSegment(segment: string | undefined | null): string {
  if (!segment) return "—";
  const s = segment.trim();
  const lower = s.toLowerCase();
  if (lower === "mid_market") return "Mid Market";
  if (lower === "smb") return "SMB";
  if (lower === "upsell") return "Upsell";
  if (lower === "empty") return "Empty";
  if (lower === "others") return "Others";
  return s;
}

export function QualityScoreBadge({ score, band }: { score?: number; band?: string }) {
  if (score == null) return <span className="text-slate-400">—</span>;
  
  let bgClass = "bg-emerald-100 text-emerald-800 border-emerald-300";
  if (score < 6) bgClass = "bg-rose-100 text-rose-800 border-rose-300";
  else if (score < 8) bgClass = "bg-amber-100 text-amber-800 border-amber-300";

  return (
    <span className={cn("inline-flex items-center px-2 py-0.5 rounded text-[11px] font-bold border whitespace-nowrap", bgClass)}>
      QC: {score}/10 ({band || "N/A"})
    </span>
  );
}

export function getLeadStageBucket(lead: SourceLead): "MQL" | "SQL" | "SQO" | "Win" | null {
  const status = (lead.lead_status || "").toLowerCase();
  const stage = (lead.stage || "").toLowerCase();
  const mod = (lead.module || "").toLowerCase();

  // 1. Strict Win definition: ONLY Closed (Won) stage/status
  if (
    status.includes("closed won") ||
    status.includes("closed (won)") ||
    stage.includes("closed won") ||
    stage === "win"
  ) {
    return "Win";
  }

  // Check if record is explicitly marked lost
  const isLost = status.includes("lost") || stage.includes("lost") || status.includes("closed lost") || status.includes("closed (lost)");

  // 2. SQO: Demo Done AND Closing Date in the future AND NOT lost
  const isDemoDone = status.includes("demo done") || stage.includes("demo done") || stage === "sqo" || status.includes("sqo");
  let hasFutureCloseDate = false;

  const closingDate = (lead as any).closing_date;
  if (closingDate) {
    const cd = new Date(closingDate.substring(0, 10));
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (cd >= today) {
      hasFutureCloseDate = true;
    }
  }

  if (isDemoDone && hasFutureCloseDate && !isLost) {
    return "SQO";
  }

  // 3. SQL: Contacts, Accounts, Deals in progress (including Onboarding, Demo Scheduled, Demo Rescheduled, etc.)
  if (
    status.includes("demo scheduled") ||
    status.includes("demo rescheduled") ||
    status.includes("rescheduled") ||
    status.includes("demo done") ||
    status.includes("no-show") ||
    status.includes("no show") ||
    status.includes("sql") ||
    status.includes("discovery call scheduled") ||
    status.includes("qualified") ||
    status.includes("onboarding") ||
    stage.includes("sql") ||
    mod === "contacts" ||
    mod === "deals" ||
    mod === "accounts"
  ) {
    return "SQL";
  }

  return "MQL";
}

export function matchesStage(lead: SourceLead, targetStage: string): boolean {
  const bucket = getLeadStageBucket(lead);

  if (targetStage === "MQL") return true;
  if (targetStage === "SQL") return bucket === "SQL" || bucket === "SQO" || bucket === "Win";
  if (targetStage === "SQO") return bucket === "SQO" || bucket === "Win";
  if (targetStage === "Win") return bucket === "Win";
  
  if (targetStage === "Demo Done") {
    const isDemoDone = (lead as any).historical_demo_done || bucket === "SQO" || bucket === "Win";
    return !!isDemoDone;
  }

  if (targetStage.startsWith("Reason: ") || targetStage.startsWith("Competitor: ")) {
    return true;
  }

  return false;
}

export interface LeadDrilldownModalProps {
  stage: string;
  leads: (SourceLead & { source?: string })[];
  monthStr?: string;
  onClose: () => void;
}

export function LeadDrilldownModal({ stage, leads, monthStr = "2026-07", onClose }: LeadDrilldownModalProps) {
  const stageFiltered = leads.filter((l) => matchesStage(l, stage));
  
  const [selectedStatus, setSelectedStatus] = useState<string>("All");
  const [selectedSource, setSelectedSource] = useState<string>("All");
  const [selectedOwner, setSelectedOwner] = useState<string>("All");
  const [qcData, setQcData] = useState<FathomQCResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [expandedLeadId, setExpandedLeadId] = useState<string | null>(null);

  // Extract unique statuses, sources, and owners for dropdown filters
  const uniqueStatuses = Array.from(
    new Set(stageFiltered.map((l) => l.lead_status).filter(Boolean))
  ).sort();

  const uniqueSources = Array.from(
    new Set(stageFiltered.map((l) => (l as any).source || l.lead_source).filter(Boolean))
  ).sort();

  const uniqueOwners = Array.from(
    new Set(stageFiltered.map((l) => l.owner).filter(Boolean))
  ).sort();

  const filtered = stageFiltered.filter((l) => {
    if (selectedStatus !== "All" && l.lead_status !== selectedStatus) return false;
    const src = (l as any).source || l.lead_source;
    if (selectedSource !== "All" && src !== selectedSource) return false;
    if (selectedOwner !== "All" && (l.owner || "Unassigned") !== selectedOwner) return false;
    return true;
  });

  const fetchQcResults = (refresh = false) => {
    setLoading(true);
    const normalizedMonth = monthStr.replace(/\//g, "-");
    const url = `/api/fathom/quality-check?month=${encodeURIComponent(normalizedMonth)}${refresh ? "&refresh=true" : ""}`;
    fetch(url)
      .then((res) => res.json())
      .then((data) => {
        if (data && data.results) {
          setQcData(data);
        }
      })
      .catch((err) => console.error("Error fetching Fathom QC:", err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (stage === "SQL") {
      fetchQcResults(false);
    }
  }, [stage, monthStr]);

  const qcMap = new Map<string, FathomQCItem>();
  if (qcData?.results) {
    qcData.results.forEach((item) => {
      if (item.email) qcMap.set(item.email.toLowerCase().trim(), item);
      if (item.lead_id) qcMap.set(String(item.lead_id), item);
      if (item.lead_name) qcMap.set(`name:${item.lead_name.toLowerCase().trim()}`, item);
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/50 backdrop-blur-sm p-0 sm:p-6" onClick={onClose}>
      <div className="flex flex-col bg-white rounded-t-2xl sm:rounded-card shadow-xl w-full max-w-6xl max-h-[85vh] sm:max-h-[90vh] overflow-hidden transition-transform" onClick={(e) => e.stopPropagation()}>
        
        <div className="flex flex-col sm:flex-row sm:items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-border shrink-0 gap-3">
          <div>
            <div className="flex items-center space-x-3">
              <h2 className="text-base sm:text-lg font-semibold text-ink-900">All {stage} Leads</h2>
              {stage === "SQL" && (
                <button
                  type="button"
                  onClick={() => fetchQcResults(true)}
                  disabled={loading}
                  className="inline-flex items-center space-x-1 px-2.5 py-1 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded text-xs font-semibold border border-blue-200 transition-colors disabled:opacity-50"
                  title="Run controlled batch Quality Check against Fathom API"
                >
                  <RefreshCw className={cn("w-3.5 h-3.5", loading && "animate-spin")} />
                  <span>{loading ? "Analyzing Fathom..." : "Run QC Batch"}</span>
                </button>
              )}
            </div>
            <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
              <span className="numeric-value font-medium">{filtered.length}</span> of <span className="numeric-value font-medium">{stageFiltered.length}</span> matching drill-down records
              {qcData && (
                <span className="ml-2 text-slate-400">
                  • Cached QC Status: <strong className="text-slate-700">{qcData.from_cache ? "Cached (Instant)" : "Fresh Batch"}</strong>
                </span>
              )}
            </p>
          </div>

          <div className="flex items-center space-x-2 shrink-0 flex-wrap gap-y-2">
            {/* Status Filter */}
            <div className="flex items-center space-x-1">
              <label className="text-xs font-medium text-slate-600">Status:</label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="text-xs bg-slate-50 border border-slate-200 rounded-md px-2 py-1 font-medium text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="All">All Statuses ({stageFiltered.filter(l => (selectedSource === "All" || ((l as any).source || l.lead_source) === selectedSource) && (selectedOwner === "All" || (l.owner || "Unassigned") === selectedOwner)).length})</option>
                {uniqueStatuses.map((st) => {
                  const cnt = stageFiltered.filter((l) => l.lead_status === st && (selectedSource === "All" || ((l as any).source || l.lead_source) === selectedSource) && (selectedOwner === "All" || (l.owner || "Unassigned") === selectedOwner)).length;
                  return (
                    <option key={st} value={st}>
                      {st} ({cnt})
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Source Filter */}
            <div className="flex items-center space-x-1">
              <label className="text-xs font-medium text-slate-600">Source:</label>
              <select
                value={selectedSource}
                onChange={(e) => setSelectedSource(e.target.value)}
                className="text-xs bg-slate-50 border border-slate-200 rounded-md px-2 py-1 font-medium text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="All">All Sources ({stageFiltered.filter(l => (selectedStatus === "All" || l.lead_status === selectedStatus) && (selectedOwner === "All" || (l.owner || "Unassigned") === selectedOwner)).length})</option>
                {uniqueSources.map((src) => {
                  const cnt = stageFiltered.filter((l) => ((l as any).source || l.lead_source) === src && (selectedStatus === "All" || l.lead_status === selectedStatus) && (selectedOwner === "All" || (l.owner || "Unassigned") === selectedOwner)).length;
                  return (
                    <option key={src} value={src}>
                      {src} ({cnt})
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Lead Owner Filter */}
            <div className="flex items-center space-x-1">
              <label className="text-xs font-medium text-slate-600">Owner:</label>
              <select
                value={selectedOwner}
                onChange={(e) => setSelectedOwner(e.target.value)}
                className="text-xs bg-slate-50 border border-slate-200 rounded-md px-2 py-1 font-medium text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500 max-w-[160px] truncate"
              >
                <option value="All">All Owners ({stageFiltered.filter(l => (selectedStatus === "All" || l.lead_status === selectedStatus) && (selectedSource === "All" || ((l as any).source || l.lead_source) === selectedSource)).length})</option>
                {uniqueOwners.map((own) => {
                  const cnt = stageFiltered.filter((l) => (l.owner || "Unassigned") === own && (selectedStatus === "All" || l.lead_status === selectedStatus) && (selectedSource === "All" || ((l as any).source || l.lead_source) === selectedSource)).length;
                  return (
                    <option key={own} value={own}>
                      {own} ({cnt})
                    </option>
                  );
                })}
              </select>
            </div>

            <button type="button" onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden p-3 sm:p-6 flex flex-col">
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-slate-500">No leads found for this stage.</div>
          ) : (
            <div className="overflow-auto rounded-md border border-border bg-white shadow-inner max-h-[60vh] sm:max-h-[65vh] [&::-webkit-scrollbar]:w-2.5 [&::-webkit-scrollbar]:h-2.5 [&::-webkit-scrollbar-thumb]:bg-slate-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-slate-100">
              <table className="w-full text-xs min-w-[950px]">
                <thead className="sticky top-0 z-10 bg-slate-50 shadow-sm">
                  <tr className="text-left label-xs text-slate-600 border-b border-border">
                    <th className="font-medium px-3 py-2.5 whitespace-nowrap min-w-[110px] bg-slate-50">Source</th>
                    <th className="font-medium px-3 py-2.5 whitespace-nowrap min-w-[130px] bg-slate-50">Name</th>
                    <th className="font-medium px-3 py-2.5 whitespace-nowrap min-w-[180px] bg-slate-50">Email</th>
                    <th className="font-medium px-3 py-2.5 whitespace-nowrap min-w-[160px] bg-slate-50">Company</th>
                    <th className="font-medium px-3 py-2.5 whitespace-nowrap min-w-[130px] bg-slate-50">Owner</th>
                    <th className="font-medium px-3 py-2.5 whitespace-nowrap min-w-[120px] bg-slate-50">Status</th>
                    {stage === "SQL" && <th className="font-medium px-3 py-2.5 whitespace-nowrap min-w-[150px] bg-slate-50">Fathom Quality Score</th>}
                    {stage === "SQL" && <th className="font-medium px-3 py-2.5 whitespace-nowrap min-w-[110px] bg-slate-50">Recording</th>}
                    <th className="font-medium px-3 py-2.5 whitespace-nowrap min-w-[110px] bg-slate-50">Demo Date</th>
                    <th className="font-medium px-3 py-2.5 whitespace-nowrap min-w-[110px] bg-slate-50">Received Date</th>
                  </tr>
                </thead>
                <tbody>
                  {[...filtered]
                    .sort((a, b) => {
                    const dateA = a.demo_date || a.received_date;
                    const dateB = b.demo_date || b.received_date;
                    return (dateB ? new Date(dateB).getTime() : 0) - (dateA ? new Date(dateA).getTime() : 0);
                  })
                    .map((lead) => {
                      const leadIdStr = String(lead.id || "");
                      const emailKey = (lead.email || "").toLowerCase().trim();
                      const nameKey = (lead.name || "").toLowerCase().trim();
                      let qcItem = (emailKey ? qcMap.get(emailKey) : null) || qcMap.get(leadIdStr) || (nameKey ? qcMap.get(`name:${nameKey}`) : null);

                      if (!qcItem) {
                        for (const item of qcData?.results || []) {
                          if (leadIdStr && String(item.lead_id) === leadIdStr) {
                            qcItem = item;
                            break;
                          }
                          if (emailKey && item.email && (item.email.toLowerCase().includes(emailKey) || emailKey.includes(item.email.toLowerCase()))) {
                            qcItem = item;
                            break;
                          }
                          if (nameKey && item.lead_name && (item.lead_name.toLowerCase().includes(nameKey) || nameKey.includes(item.lead_name.toLowerCase()))) {
                            qcItem = item;
                            break;
                          }
                        }
                      }

                      const isExpanded = expandedLeadId === lead.id;

                      return (
                        <>
                          <tr key={`${lead.module}-${lead.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="px-3 py-2 font-medium text-slate-700 whitespace-nowrap">{lead.source}</td>
                            <td className="px-3 py-2 font-medium text-ink-900 whitespace-nowrap">
                              <a href={lead.zoho_url} target="_blank" rel="noreferrer" className="hover:underline text-blue-600">
                                {lead.name || "—"}
                              </a>
                            </td>
                            <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{lead.email || "—"}</td>
                            <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{lead.company || "—"}</td>
                            <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{lead.owner || "—"}</td>
                            <td className="px-3 py-2 whitespace-nowrap"><StatusBadge status={lead.lead_status} /></td>
                            
                            {stage === "SQL" && (
                              <td className="px-3 py-2 whitespace-nowrap cursor-pointer" onClick={() => setExpandedLeadId(isExpanded ? null : lead.id)}>
                                {qcItem ? (
                                  <QualityScoreBadge score={qcItem.overall_quality_score} band={qcItem.quality_band} />
                                ) : (
                                  <QualityScoreBadge score={5} band="Unverified" />
                                )}
                              </td>
                            )}

                            {stage === "SQL" && (
                              <td className="px-3 py-2 whitespace-nowrap">
                                {qcItem?.fathom_recording_url ? (
                                  <a
                                    href={qcItem.fathom_recording_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center space-x-1 text-xs text-blue-600 font-semibold hover:underline"
                                  >
                                    <Video className="w-3.5 h-3.5 text-blue-500" />
                                    <span>Watch Call</span>
                                    <ExternalLink className="w-3 h-3" />
                                  </a>
                                ) : (
                                  <span className="text-slate-400">—</span>
                                )}
                              </td>
                            )}

                            <td className="px-3 py-2 text-slate-600 whitespace-nowrap">
                              {lead.demo_date ? formatDate(lead.demo_date) : "—"}
                            </td>
                            <td className="px-3 py-2 text-slate-600 whitespace-nowrap">
                              {lead.received_date ? formatDate(lead.received_date) : "—"}
                            </td>
                          </tr>

                          {/* Expanded Details Row for Quality Check Details */}
                          {stage === "SQL" && isExpanded && qcItem && (
                            <tr className="bg-slate-50/80 border-b border-slate-200">
                              <td colSpan={9} className="px-6 py-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                                  <div>
                                    <h4 className="font-semibold text-slate-900 flex items-center space-x-1 mb-1">
                                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                                      <span>Key Takeaways & Call Fit</span>
                                    </h4>
                                    <ul className="list-disc list-inside space-y-1 text-slate-700">
                                      {qcItem.key_takeaways.map((t, idx) => (
                                        <li key={idx}>{t}</li>
                                      ))}
                                    </ul>
                                    <div className="mt-2 text-slate-700">
                                      <strong>Recommended Action:</strong> {qcItem.recommended_action}
                                    </div>
                                  </div>

                                  <div>
                                    <h4 className="font-semibold text-slate-900 flex items-center space-x-1 mb-1">
                                      <AlertTriangle className="w-4 h-4 text-amber-600" />
                                      <span>Call Criteria & Risk Flags</span>
                                    </h4>
                                    <div className="space-y-1 text-slate-700">
                                      <div><strong>Product Fit:</strong> {qcItem.product_fit}</div>
                                      <div><strong>Buying Intent:</strong> {qcItem.buying_intent}</div>
                                      <div><strong>Budget Discussed:</strong> {qcItem.budget_discussed ? "Yes" : "No"}</div>
                                      <div><strong>Next Steps Confirmed:</strong> {qcItem.next_step_confirmed ? "Yes" : "No"}</div>
                                    </div>
                                    {qcItem.risk_flags.length > 0 && (
                                      <div className="mt-2 text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">
                                        <strong>Risk Flags:</strong> {qcItem.risk_flags.join(", ")}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
